import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  BsShieldLockFill,
  BsEyeSlashFill,
  BsDatabaseFillCheck,
  BsShareFill,
  BsCookie,
  BsArrowLeftShort,
  BsFileTextFill,
  BsEnvelopeFill,
} from "react-icons/bs";

function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "Data Encryption & Digital Privacy Policy | NGO Waters";
  }, []);

  const dataProtocols = [
    {
      icon: (
        <BsEyeSlashFill
          className="fs-5"
          style={{ color: "var(--brand-mineral-aqua)" }}
        />
      ),
      title: "Data Telemetry We Map",
      text: "We collect precise logistical information (corporate names, verified delivery coordinates, and transaction logs) along with non-personal hardware strings to strictly stabilize digital wholesale checkout loops.",
    },
    {
      icon: <BsDatabaseFillCheck className="fs-5 text-success" />,
      title: "How We Utilize Vaulted Data",
      text: "Information blocks are strictly funneled to handle batch delivery scheduling under automated systems, verify corporate identity records, and maintain continuous factory-to-distributor transport visibility.",
    },
    {
      icon: <BsShareFill className="fs-5 text-danger" />,
      title: "Zero Third-Party Arbitrage",
      text: "NGO Waters maintains an absolute zero-rental framework. Your corporate data blocks are never leased or liquidated to outbound advertising networks. Allocation is limited solely to verified transport units.",
    },
  ];

  return (
    <motion.div
      className="container-fluid min-vh-100 position-relative overflow-hidden py-5 px-3 px-md-5 bg-light"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      {/* Low-Opacity Depth Enhancement Ambient Blur Blobs */}
      <div
        className="position-absolute rounded-circle"
        style={{
          width: "550px",
          height: "550px",
          background: "rgba(54, 191, 216, 0.03)",
          top: "-200px",
          right: "-50px",
          filter: "blur(130px)",
          pointerEvents: "none",
        }}
      />
      <div
        className="position-absolute rounded-circle"
        style={{
          width: "450px",
          height: "450px",
          background: "rgba(166, 123, 91, 0.02)",
          bottom: "12%",
          left: "-80px",
          filter: "blur(110px)",
          pointerEvents: "none",
        }}
      />

      {/* Production-Ready Technical SEO / GEO / AEO Schema Injection */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "NGO Waters Corporate Privacy and Data Security Framework",
          description:
            "Cryptographic protection matrices, data management parameters, and corporate safety bounds under Ponia Food And Beverage Private Limited.",
          publisher: {
            "@type": "Organization",
            name: "PONIA FOOD AND BEVERAGE PRIVATE LIMITED",
            duns: "CIN: U11043UP2024PTC213517",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Village - Subakara, Thana - Gonda",
              addressLocality: "Aligarh",
              addressRegion: "Uttar Pradesh",
              postalCode: "202123",
              addressCountry: "IN",
            },
          },
        })}
      </script>

      {/* ================= DATA PRIVACY HERO HEADER ================= */}
      <section className="container py-4 position-relative z-1 mb-5 text-center">
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span
            className="badge px-3 py-2 mb-3 text-uppercase fw-semibold tracking-wider"
            style={{
              backgroundColor: "rgba(26, 60, 107, 0.06)",
              color: "var(--brand-deep-blue)",
              fontSize: "0.72rem",
              borderRadius: "6px",
            }}
          >
            Cryptographic Data Isolation Specs
          </span>
          <h1
            className="display-5 fw-bold mb-3"
            style={{ color: "var(--brand-deep-blue)", letterSpacing: "-1.5px" }}
          >
            Privacy Architecture
          </h1>
          <p
            className="text-secondary small mx-auto mb-0"
            style={{ maxWidth: "580px", lineHeight: "1.7" }}
          >
            Meticulously outlining individual data rights, server encapsulation
            systems, and operational security parameters for our decentralized
            bulk network.
          </p>
        </motion.div>
      </section>

      {/* ================= INTERACTIVE PRIVACY CRITERIA GRID ================= */}
      <section className="container mb-5 position-relative z-1">
        <div className="row g-4 justify-content-center">
          {dataProtocols.map((protocol, index) => (
            <div className="col-md-4" key={index}>
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{
                  y: -5,
                  boxShadow: "0px 10px 30px rgba(0,0,0,0.02)",
                }}
                className="card border-0 h-100 bg-white p-4"
                style={{
                  borderRadius: "24px",
                  border: "1px solid rgba(0,0,0,0.03)",
                  transition: "transform 0.25s ease",
                }}
              >
                <div
                  className="rounded-3 d-flex align-items-center justify-content-center mb-3"
                  style={{
                    backgroundColor: "rgba(0,0,0,0.012)",
                    width: "42px",
                    height: "42px",
                  }}
                >
                  {protocol.icon}
                </div>
                <h5
                  className="fw-bold text-dark mb-3"
                  style={{ fontSize: "1.02rem", lineHeight: "1.3" }}
                >
                  {protocol.title}
                </h5>
                <p
                  className="text-muted small mb-0"
                  style={{ lineHeight: "1.65" }}
                >
                  {protocol.text}
                </p>
              </motion.div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= COMPLEX REGULATORY LEGAL TEXT SECTION ================= */}
      <section className="container my-5 position-relative z-1">
        <div className="row justify-content-center">
          <div className="col-lg-12 col-xl-10">
            <div
              className="p-4 p-md-5 bg-white border"
              style={{ borderRadius: "24px", borderColor: "rgba(0,0,0,0.03)" }}
            >
              <div className="d-flex align-items-center gap-2 mb-4 pb-2 border-bottom border-light">
                <BsFileTextFill className="text-muted fs-5" />
                <h4
                  className="fw-bold mb-0 fs-5"
                  style={{ color: "var(--brand-deep-blue)" }}
                >
                  Corporate Security Clauses & Data Controls
                </h4>
              </div>

              <div
                className="d-flex flex-column gap-4 small text-secondary"
                style={{ lineHeight: "1.75" }}
              >
                <div>
                  <h6 className="fw-bold text-dark mb-2">
                    1. Industrial Ledger Security Standards
                  </h6>
                  <p className="mb-0">
                    We deploy custom structural isolation walls and
                    transport-layer encryption scripts to completely defend your
                    financial identifiers. Even with robust layers active,
                    absolute cryptographic safety on a global node remains a
                    statistical impossibility; hence, we restrict structural
                    storage access solely to high-clearance administration
                    machines under the legal framework of{" "}
                    <strong>PONIA FOOD AND BEVERAGE PRIVATE LIMITED</strong>.
                  </p>
                </div>

                <div>
                  <h6 className="fw-bold text-dark mb-2">
                    2. Token Cookie Operations
                  </h6>
                  <p className="mb-0">
                    Our application stack activates temporary alpha-numeric
                    cookie strings to maintain operational state continuity,
                    save dashboard configurations, and shield active B2B
                    enterprise procurement maps. Users hold individual access
                    authority to disable tracking modules via browser
                    preferences, with an acknowledgement that this action can
                    restrict certain checkout scripts.
                  </p>
                </div>

                <div>
                  <h6 className="fw-bold text-dark mb-2">
                    3. Policy Revisions & Dynamic Adjustments
                  </h6>
                  <p className="mb-0">
                    NGO Waters preserves absolute authority to reconfigure or
                    refresh this privacy layout systematically to lock in
                    lock-step alignment with changing digital compliance
                    mandates. Every dynamic policy structural upgrade will
                    immediately append its revised validation sequence stamp
                    right on this active coordinate.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= COMPLIANCE CONTACT ESCALATION BANNER ================= */}
      <section className="container my-5 position-relative z-1">
        <div className="row justify-content-center">
          <div className="col-lg-12 col-xl-10">
            <div
              className="bg-white border p-4 p-md-5 text-center text-md-start"
              style={{
                borderRadius: "24px",
                borderColor: "rgba(0,0,0,0.04)",
                borderLeft: "5px solid var(--brand-copper)",
              }}
            >
              <div className="row align-items-center g-4">
                <div className="col-md-8">
                  <div className="d-flex align-items-center gap-2 mb-2 justify-content-center justify-content-md-start">
                    <BsShieldLockFill
                      style={{ color: "var(--brand-deep-blue)" }}
                      className="fs-5"
                    />
                    <h5
                      className="fw-bold mb-0 text-dark"
                      style={{ fontSize: "1.1rem" }}
                    >
                      Data Protection Protection Desk
                    </h5>
                  </div>
                  <p
                    className="text-muted small mb-0"
                    style={{ lineHeight: "1.6" }}
                  >
                    To issue a systematic right-to-erase script request, query
                    internal tracking servers, or log structural database
                    complaints, directly channel your ticket to our compliance
                    office.
                  </p>
                </div>
                <div className="col-md-4 text-md-end">
                  <div className="d-flex flex-column flex-sm-row gap-2 justify-content-md-end">
                    <Link
                      to="/contact"
                      className="btn btn-outline-dark px-4 py-2.5 fw-bold"
                      style={{ borderRadius: "10px", fontSize: "0.85rem" }}
                    >
                      Open Support Ticket
                    </Link>
                    <a
                      href="mailto:support@ngowaters.com"
                      className="btn text-white px-4 py-2.5 fw-bold border-0 d-inline-flex align-items-center justify-content-center gap-2"
                      style={{
                        backgroundColor: "var(--brand-deep-blue)",
                        borderRadius: "10px",
                        fontSize: "0.85rem",
                      }}
                    >
                      <BsEnvelopeFill /> Email Desk
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= SYSTEM APP EXIT FLOW CONTROLLER ================= */}
      <div className="text-center mt-5 position-relative z-1">
        <Link
          to="/"
          className="btn btn-sm btn-light border px-4 py-2 fw-semibold text-secondary d-inline-flex align-items-center gap-1"
          style={{ borderRadius: "12px", fontSize: "0.82rem" }}
        >
          <BsArrowLeftShort className="fs-5" /> Return To Home Core
        </Link>
      </div>
    </motion.div>
  );
}

export default PrivacyPolicy;
